import { Box, Group, Stack, Text } from '@mantine/core';
import { Activity } from 'react-feather';
import { ContentCard } from '../../../../components/common/ContentCard';

/**
 * MED-1194 (B10) — the "In-session insights" card: fixed interpretation copy
 * for what happened during the session, rendered on the coach and
 * session-leader post-session views (never the participant view). The server
 * selects the messages off the STORED airtime reading (the same source the
 * C2B pill reads), view-scoped: the one-participant-dominating message is
 * coach-only and never reaches the peer-leader wire.
 *
 * Copy is verbatim client copy (§6, resolved 7/13) — fixed strings, never
 * personalized, no names. Availability follows the B7/B9 pattern: card absent
 * when AI wasn't active or no usable airtime exists (AC5 — nothing renders,
 * no empty state). Pacing insights are a follow-up (no nudge count is
 * persisted today — see ticket).
 */

const BAND_DIVIDER = '#D1D5DB';
const VALUE_COLOR = '#374151';
const SUBCARD_BG = '#EDF5F0';

export interface InSessionInsightsEnvelope {
  status: 'available' | 'not_available';
  messages?: Array<{
    kind: 'airtime_split' | 'airtime_dominating';
    title: string;
    text: string;
  }>;
}

interface Props {
  /** The bundle's insights envelope. Null = AI wasn't active (card absent). */
  insights: InSessionInsightsEnvelope | null;
  /** MED-1128 effective gate — modality AI && session listening. */
  aiAvailable: boolean;
}

export function InSessionInsightsCard({ insights, aiAvailable }: Props) {
  if (!aiAvailable || insights == null) return null;
  if (insights.status !== 'available') return null;
  const messages = insights.messages ?? [];
  if (messages.length === 0) return null;

  return (
    <ContentCard
      variant="elevated"
      padding={0}
      contentGap={0}
      fill={false}
      header={null}
      data-testid="insession-insights-card"
      style={{ overflow: 'hidden' }}
    >
      <Group
        gap={8}
        align="center"
        wrap="nowrap"
        style={{ padding: '16px 20px', borderBottom: `1px solid ${BAND_DIVIDER}` }}
      >
        <Activity size={15} color={VALUE_COLOR} />
        <Text fw={700} fz={14} c={VALUE_COLOR}>
          In-session insights
        </Text>
      </Group>

      <Stack gap={12} style={{ padding: 20 }}>
        {messages.map((m, i) => (
          <Box
            key={i}
            style={{ backgroundColor: SUBCARD_BG, borderRadius: 8, padding: '12px 16px' }}
          >
            <Text fw={700} fz={13} c={VALUE_COLOR} mb={4}>
              {m.title}
            </Text>
            <Text fz={13} c={VALUE_COLOR}>
              {m.text}
            </Text>
          </Box>
        ))}
      </Stack>
    </ContentCard>
  );
}

export default InSessionInsightsCard;
